import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, DatePicker, Button, message, Upload } from 'antd';
import { InboxOutlined, DeleteOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { FormInput, CustomInput, Text } from 'atoms';
import ProgressBar from 'components/ProgressBar';
import { getSignedUrl } from 'store/commonSlice';

const StyledModal = styled(Modal)`
	.ant-modal-content {
		border-radius: 4px;
	}
	.ant-modal-body {
		padding: 24px;
	}
`;

const ButtonContainer = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 24px;
	padding-top: 24px;
	border-top: 1px solid #f0f0f0;
`;

const CancelButton = styled(Button)`
	border: 1px solid #143e60;
	color: #143e60;
	background: #ffffff;
	&:hover {
		border-color: #1a4d7a;
		color: #1a4d7a;
		background: #ffffff;
	}
`;

const SubmitButton = styled(Button)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover {
		background: #1a4d7a;
		color: #ffffff;
	}
`;

const PaymentProgressSection = styled.div`
	margin-bottom: 24px;
	padding-bottom: 20px;
	border-bottom: 1px solid #f0f0f0;
`;

const PaymentProgressHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 16px;
`;

const PaymentProgressTitle = styled(Text)`
	font-size: 16px;
	font-weight: 600;
	color: #333;
`;

const PaymentPercentage = styled(Text)`
	font-size: 16px;
	font-weight: 600;
	color: #143e60;
`;

const PaymentBreakdown = styled.div`
	display: flex;
	gap: 24px;
	margin-bottom: 12px;
`;

const PaymentItem = styled.div`
	flex: 1;
`;

const PaymentItemLabel = styled(Text)`
	font-size: 12px;
	color: #666;
	display: block;
	margin-bottom: 4px;
`;

const PaymentItemValue = styled(Text)`
	font-size: 18px;
	font-weight: 700;
	display: block;
	color: ${(props) => props.color || '#333'};
`;

const ProgressBarWrapper = styled.div`
	margin-top: 12px;
	background-color: #eefff1;
	border-radius: 4px;
	overflow: hidden;
`;

const DocumentUploadSection = styled.div`
	margin-top: 24px;
	margin-bottom: 16px;
`;

const DocumentUploadTitle = styled(Text)`
	font-size: 14px;
	font-weight: 600;
	color: #333;
	display: block;
	margin-bottom: 12px;
`;

const UploadDragger = styled(Upload.Dragger)`
	.ant-upload-drag {
		background: #fafafa;
		border: 1px dashed #d9d9d9;
		border-radius: 4px;
	}
	.ant-upload-drag:hover {
		border-color: #143e60;
	}
`;

const FileListContainer = styled.div`
	margin-top: 12px;
`;

const FileItem = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 8px 12px;
	background: #f5f5f5;
	border-radius: 4px;
	margin-bottom: 8px;
`;

const FileInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	flex: 1;
`;

/**
 * Pure helper — builds the API payload for the payment submit.
 * Extracted here (module level) to keep handleSubmit cognitive complexity ≤ 15.
 * Both v1 and v2 vendor invoice paths use the same payload shape.
 */
const buildSubmitData = ({ vendorInvoice, values, record, vendorBill, uploadedFile }) => {
	if (!vendorInvoice) {
		// Old format for finance/payments page (non-vendor-invoice)
		return {
			finance_request_id: record?.id,
			paid_date: moment(values.paid_date).format('YYYY-MM-DD'),
			utr_number: values.utr_number.trim(),
			paid_amount: parseFloat(values.paid_amount),
		};
	}
	// Vendor invoice format — same for both v1 and v2:
	// image_keys as array (optional), no status field, MM/DD/YYYY date
	return {
		vendor_bill_id: vendorBill?.id || vendorBill?.vendor_bill_id,
		paid_amount: parseFloat(values.paid_amount),
		utr_number: values.utr_number.trim(),
		paid_date: moment(values.paid_date).format('MM/DD/YYYY'),
		...(uploadedFile?.key && { image_keys: [uploadedFile.key] }),
	};
};

const getValidationSchema = (vendorInvoice, remainingBalance) => {
	const baseSchema = {
		paid_date: Yup.date()
			.required('Paid Date is required')
			.nullable()
			.typeError('Please select a valid date'),
		utr_number: Yup.string().required('UTR No is required').trim(),
		paid_amount: Yup.number()
			.typeError('Paid Amount must be a number')
			.required('Paid Amount is required')
			.positive('Paid Amount must be greater than 0')
			.min(0.01, 'Paid Amount must be greater than 0'),
	};

	if (vendorInvoice && remainingBalance !== undefined) {
		baseSchema.paid_amount = baseSchema.paid_amount.max(
			remainingBalance,
			`Payment amount exceeds remaining balance. Maximum allowed: ₹${remainingBalance.toLocaleString(
				'en-IN',
				{
					minimumFractionDigits: 2,
					maximumFractionDigits: 2,
				}
			)}`
		);
	}

	return Yup.object().shape(baseSchema);
};

function PaymentDetailsModal({
	visible,
	onClose,
	record,
	onSubmit,
	loading = false,
	vendorInvoice = false,
	vendorBill = null,
	existingPayments = [],
}) {
	const dispatch = useDispatch();
	const [uploading, setUploading] = useState(false);
	const [uploadedFile, setUploadedFile] = useState(null);

	// Helper to parse numeric value (handles string and number)
	const parseNumericValue = (value, defaultValue = 0) => {
		if (value === null || value === undefined || value === '') return defaultValue;
		if (typeof value === 'number') return value;
		if (typeof value === 'string') {
			const parsed = parseFloat(value);
			return Number.isNaN(parsed) ? defaultValue : parsed;
		}
		return defaultValue;
	};

	// Calculate payment progress (only for vendor invoice)
	// Uses same keys as InvoiceSummaryCard: total_payable_amount, paid_amount, pending_amount, paid_amount_percentage
	const calculatePaymentProgress = () => {
		if (!vendorInvoice || !vendorBill) {
			return {
				totalAmount: 0,
				paidAmount: 0,
				remainingBalance: 0,
				paymentPercentage: 0,
			};
		}

		// Use same keys as InvoiceSummaryCard
		const totalAmount = parseNumericValue(vendorBill?.total_payable_amount, 0);
		const paidAmount = parseNumericValue(vendorBill?.paid_amount, 0);
		const pendingAmount = parseNumericValue(vendorBill?.pending_amount, 0);
		const paymentPercentage = parseNumericValue(vendorBill?.paid_amount_percentage, 0);

		// Use pending_amount as remainingBalance to match InvoiceSummaryCard
		const remainingBalance = pendingAmount;

		return {
			totalAmount,
			paidAmount,
			remainingBalance,
			paymentPercentage,
		};
	};

	const paymentProgress = calculatePaymentProgress();

	// Check for pending payments
	const hasPendingPayment =
		vendorInvoice &&
		Array.isArray(existingPayments) &&
		existingPayments.some((p) => p.status === 'pending');

	// Calculate initial paid amount
	const getInitialPaidAmount = () => {
		if (vendorInvoice) {
			if (paymentProgress.remainingBalance > 0) {
				return paymentProgress.remainingBalance;
			}
			return record?.request_value || '';
		}
		return record?.request_value || '';
	};

	const initialValues = {
		paid_date: null,
		utr_number: '',
		paid_amount: getInitialPaidAmount(),
		document_key: null,
	};

	const handleFileUpload = async (file) => {
		// Validate file type
		const allowedTypes = [
			'application/pdf',
			'image/png',
			'image/jpeg',
			'image/jpg',
			'application/msword',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		];
		const maxSizeMB = 10;

		if (!allowedTypes.includes(file.type)) {
			message.error('You can only upload PDF, Images (PNG, JPG, JPEG), or DOC files!');
			return false;
		}

		if (file.size / 1024 / 1024 > maxSizeMB) {
			message.error(`File must be smaller than ${maxSizeMB}MB!`);
			return false;
		}

		setUploading(true);

		try {
			// Get signed URL
			const signedUrlResponse = await dispatch(
				getSignedUrl({
					url_type: 'upload',
					data: {
						entity: 'invoice_payment',
						keys: [file.name],
					},
				})
			);

			const fileData = signedUrlResponse?.data?.[0];
			if (!fileData?.url || !fileData?.filename) {
				throw new Error('Failed to get signed URL');
			}

			// Upload file to S3
			const uploadResponse = await fetch(fileData.url, {
				method: 'PUT',
				body: file,
				headers: {
					'Content-Type': file.type,
				},
			});

			if (!uploadResponse.ok) {
				throw new Error('Failed to upload file to S3');
			}

			// Store file info
			setUploadedFile({
				name: file.name,
				size: file.size,
				key: fileData.filename,
			});

			message.success('File uploaded successfully');
			return false; // Prevent default upload behavior
		} catch (error) {
			console.error('File upload error:', error);
			message.error('Failed to upload file');
			return false;
		} finally {
			setUploading(false);
		}
	};

	const handleFileRemove = () => {
		setUploadedFile(null);
		message.success('File removed');
	};

	const handleSubmit = (values, { setSubmitting, resetForm }) => {
		if (!values.paid_date) {
			message.error('Please select a paid date');
			setSubmitting(false);
			return;
		}

		// Check for pending payments (blocking rule)
		if (vendorInvoice && hasPendingPayment) {
			message.error(
				'Cannot add new payment. An existing payment with status "pending" must be processed first.'
			);
			setSubmitting(false);
			return;
		}

		// Validate overpayment
		if (vendorInvoice && paymentProgress.remainingBalance > 0) {
			const paymentAmount = parseFloat(values.paid_amount);
			if (paymentAmount > paymentProgress.remainingBalance) {
				message.error(
					`Payment amount exceeds remaining balance. Maximum allowed: ₹${paymentProgress.remainingBalance.toLocaleString(
						'en-IN',
						{
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
						}
					)}`
				);
				setSubmitting(false);
				return;
			}
		}

		const submitData = buildSubmitData({
			vendorInvoice,
			values,
			record,
			vendorBill,
			uploadedFile,
		});

		onSubmit(submitData)
			.then(() => {
				resetForm();
				setUploadedFile(null);
			})
			.catch((error) => {
				message.error(error.message || 'Failed to submit payment details');
				setSubmitting(false);
			});
	};

	const handleCancel = (resetForm) => {
		resetForm();
		setUploadedFile(null);
		onClose();
	};

	const formatCurrency = (amount) =>
		`₹${amount.toLocaleString('en-IN', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;

	const modalTitle = vendorInvoice
		? `Mark Payment - ${vendorBill?.vendor_bill_no || 'Invoice'}`
		: 'Payment Details';

	return (
		<StyledModal
			title={modalTitle}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={vendorInvoice ? 600 : 500}
			centered
			destroyOnClose
			maskClosable={false}
		>
			<Formik
				initialValues={initialValues}
				validationSchema={getValidationSchema(
					vendorInvoice,
					paymentProgress.remainingBalance
				)}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ values, setFieldValue, errors, touched, isSubmitting, resetForm }) => (
					<Form layout="vertical">
						{/* Payment Progress Section - Only for vendor invoice */}
						{vendorInvoice && vendorBill && (
							<PaymentProgressSection>
								<PaymentProgressHeader>
									<PaymentProgressTitle>Payment Progress</PaymentProgressTitle>
									<PaymentPercentage>
										{Math.round(paymentProgress.paymentPercentage)}% Paid
									</PaymentPercentage>
								</PaymentProgressHeader>
								<PaymentBreakdown>
									<PaymentItem>
										<PaymentItemLabel>Total Amount</PaymentItemLabel>
										<PaymentItemValue>
											{formatCurrency(paymentProgress.totalAmount)}
										</PaymentItemValue>
									</PaymentItem>
									<PaymentItem>
										<PaymentItemLabel>Paid</PaymentItemLabel>
										<PaymentItemValue color="#4caf50">
											{formatCurrency(paymentProgress.paidAmount)}
										</PaymentItemValue>
									</PaymentItem>
									<PaymentItem>
										<PaymentItemLabel>Remaining</PaymentItemLabel>
										<PaymentItemValue color="#ff9800">
											{formatCurrency(paymentProgress.remainingBalance)}
										</PaymentItemValue>
									</PaymentItem>
								</PaymentBreakdown>
								<ProgressBarWrapper>
									<ProgressBar
										percentage={paymentProgress.paymentPercentage}
										height={8}
										spliting={4}
									/>
								</ProgressBarWrapper>
							</PaymentProgressSection>
						)}

						<FormInput
							name="paid_date"
							label="Payment Date*"
							error={touched.paid_date && errors.paid_date}
						>
							<DatePicker
								name="paid_date"
								value={values.paid_date ? moment(values.paid_date) : null}
								onChange={(date) => {
									setFieldValue('paid_date', date ? date.toDate() : null, false);
								}}
								style={{ width: '100%' }}
								format="DD/MM/YYYY"
								placeholder="dd/mm/yyyy"
								disabledDate={(current) =>
									current && current > moment().endOf('day')
								}
							/>
						</FormInput>

						<FormInput
							name="utr_number"
							label="UTR Number*"
							error={touched.utr_number && errors.utr_number}
						>
							<CustomInput
								name="utr_number"
								placeholder="Enter UTR/Transaction Reference"
							/>
						</FormInput>

						<FormInput
							name="paid_amount"
							label="Payment Amount*"
							error={touched.paid_amount && errors.paid_amount}
						>
							<CustomInput
								name="paid_amount"
								type="number"
								placeholder={
									vendorInvoice
										? `₹ Max: ${paymentProgress.remainingBalance.toLocaleString(
												'en-IN',
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												}
										  )}`
										: 'Enter paid amount'
								}
								step="0.01"
								min="0"
								disabled={!vendorInvoice}
							/>
						</FormInput>

						{/* Payment Proof Documents Section - Only for vendor invoice */}
						{vendorInvoice && (
							<DocumentUploadSection>
								<DocumentUploadTitle>Payment Proof Documents</DocumentUploadTitle>
								<UploadDragger
									beforeUpload={handleFileUpload}
									showUploadList={false}
									accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
									disabled={uploading || !!uploadedFile}
								>
									<p className="ant-upload-drag-icon">
										<InboxOutlined />
									</p>
									<p className="ant-upload-text">
										Click to upload or drag and drop PDF, Images, DOC up to 10MB
									</p>
								</UploadDragger>
								{uploadedFile && (
									<FileListContainer>
										<FileItem>
											<FileInfo>
												<Text>{uploadedFile.name}</Text>
												<Text type="secondary">
													({(uploadedFile.size / 1024 / 1024).toFixed(2)}{' '}
													MB)
												</Text>
											</FileInfo>
											<Button
												type="link"
												icon={<DeleteOutlined />}
												onClick={handleFileRemove}
												danger
											>
												Remove
											</Button>
										</FileItem>
									</FileListContainer>
								)}
							</DocumentUploadSection>
						)}

						<ButtonContainer>
							<CancelButton onClick={() => handleCancel(resetForm)}>
								Cancel
							</CancelButton>
							<SubmitButton
								type="submit"
								htmlType="submit"
								loading={isSubmitting || loading || uploading}
							>
								{vendorInvoice ? 'Submit Payment' : 'Submit'}
							</SubmitButton>
						</ButtonContainer>
					</Form>
				)}
			</Formik>
		</StyledModal>
	);
}

PaymentDetailsModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	record: PropTypes.object.isRequired,
	onSubmit: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	vendorInvoice: PropTypes.bool,
	vendorBill: PropTypes.object,
	existingPayments: PropTypes.array,
};

export default PaymentDetailsModal;
